import type { IBeneficiaryRepository } from "@/application/ports/repositories";
import type {
	BeneficiaryDetail,
	BeneficiaryDetailResponse,
	FiscalQuota,
	FiscalYearSummary,
	CreateFiscalQuotaRequest,
	UpdateFiscalQuotaRequest,
	BeneficiaryMetrics,
	BeneficiaryUpdateRequest,
	CreateBeneficiaryResponse,
	BeneficiaryListParams,
	BeneficiaryMetricsParams,
} from "@/domain";
import type { PaginatedBeneficiaries } from "@/domain/beneficiary";
import type {
	DocumentWithReviewResponse,
	DocumentDownloadResponse,
	BeneficiaryPortfolioResponse,
	ReviewUpdateRequest,
	DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { ListDocumentsParams } from "@/domain/document";
import { DocumentType, EntityType } from "@/domain/documentEnums";
import { downloadBlob } from "../fileDownload";
import { apiClient, ApiClientError } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class BeneficiaryRepository implements IBeneficiaryRepository {
	private basePath = "/beneficiaries";
	// --- Document helper methods (inlined from documentService) ---

	private async uploadBankCertificateDocument(
		beneficiaryUuid: string,
		bankAccountUuid: string,
		certificate: File,
	): Promise<DocumentWithReviewResponse> {
		const formData = new FormData();
		formData.append("document", certificate);
		formData.append("document_type", DocumentType.BANK_ACCOUNT_CERTIFICATE);
		formData.append("entity_type", EntityType.BENEFICIARY_BANK_ACCOUNT);
		formData.append("entity_uuid", bankAccountUuid);
		formData.append("title", "Bank Account Certificate");

		const response = await apiClient.upload<DocumentWithReviewResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents`,
			formData,
		);
		return response.data;
	}

	private async findBankCertificate(
		beneficiaryUuid: string,
		bankAccountUuid: string,
	): Promise<DocumentWithReviewResponse | null> {
		const queryParams = new URLSearchParams();
		queryParams.append("document_type", DocumentType.BANK_ACCOUNT_CERTIFICATE);
		queryParams.append("limit", "500");

		const response = await apiClient.get<BeneficiaryPortfolioResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents?${queryParams.toString()}`,
		);
		const group = response.data.from_bank_accounts.find(
			(g) => g.bank_account_uuid === bankAccountUuid,
		);
		return group?.documents[0] ?? null;
	}

	// --- Beneficiary creation ---

	async createBeneficiary(
		data: Parameters<IBeneficiaryRepository["createBeneficiary"]>[0],
	): Promise<CreateBeneficiaryResponse> {
		const logger = getLogger();
		try {
			const basicBeneficiaryData = {
				name: data.name,
				tax_id: data.tax_id,
				company_type: data.company_type,
				main_activities: data.main_activities,
				constitution_date: data.constitution_date || null,
				address: data.address || null,
				city: data.city || null,
				state: data.state || null,
				postal_code: data.postal_code || null,
				country: data.country || null,
				phone: data.phone || null,
				email: data.email || null,
				website: data.website || null,
				business_purpose: data.business_purpose || null,
				cnae_code: data.cnae_code || null,
				last_year_revenue: data.last_year_revenue || null,
				employee_count: data.employee_count ?? null,
				incentive_type: data.incentive_type || null,
			};

			const response = await apiClient.post<CreateBeneficiaryResponse>(
				this.basePath,
				basicBeneficiaryData,
				{ headers: { "Content-Type": "application/json" } },
			);

			const beneficiaryId = response.data.uuid;

			if (data.legal_representative) {
				await apiClient.post(
					`/beneficiaries/${beneficiaryId}/legal-representatives`,
					data.legal_representative,
					{ headers: { "Content-Type": "application/json" } },
				);
			}

			if (data.banking_info) {
				try {
					const { certificate, ...bankingData } = data.banking_info;
					const bankAccountResponse = await apiClient.post<{ uuid: string }>(
						`/beneficiaries/${beneficiaryId}/bank-accounts`,
						bankingData,
						{ headers: { "Content-Type": "application/json" } },
					);
					if (certificate) {
						await this.uploadBankCertificateDocument(
							beneficiaryId,
							bankAccountResponse.data.uuid,
							certificate,
						);
					}
				} catch (error) {
					logger.error("Error adding banking info:", error);
					throw error;
				}
			}

			return response.data;
		} catch (error) {
			logger.error("Error in sequential beneficiary creation:", error);
			throw error;
		}
	}

	// --- Metrics ---

	async getBeneficiaryMetrics(
		params?: BeneficiaryMetricsParams,
	): Promise<BeneficiaryMetrics> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.sector) queryParams.append("sector", params.sector);
		}

		const url = queryParams.toString()
			? `${this.basePath}/metrics?${queryParams.toString()}`
			: `${this.basePath}/metrics`;

		const response = await apiClient.get<BeneficiaryMetrics>(url, {
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		});
		return response.data;
	}

	// --- CRUD ---

	async getBeneficiaries(params?: BeneficiaryListParams): Promise<{
		beneficiaries: BeneficiaryDetail[];
		total: number;
		page: number;
		total_pages: number;
	}> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.sector) queryParams.append("sector", params.sector);
			if (params.sort_by) queryParams.append("sort_by", params.sort_by);
			if (params.sort_direction)
				queryParams.append("sort_direction", params.sort_direction);
			if (params.limit !== undefined)
				queryParams.append("limit", params.limit.toString());
			if (params.offset !== undefined)
				queryParams.append("offset", params.offset.toString());
		}

		const url = queryParams.toString()
			? `${this.basePath}?${queryParams.toString()}`
			: this.basePath;

		const response = await apiClient.get<PaginatedBeneficiaries>(url, {
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		});

		const limit = params?.limit || 100;
		const offset = params?.offset || 0;
		const currentPage = Math.floor(offset / limit) + 1;
		const totalPages = Math.ceil((response.data.total_count || 0) / limit);

		return {
			beneficiaries: response.data.beneficiaries,
			total: response.data.total_count || response.data.beneficiaries.length,
			page: currentPage,
			total_pages: totalPages,
		};
	}

	async getBeneficiary(id: string): Promise<BeneficiaryDetailResponse> {
		const response = await apiClient.get<BeneficiaryDetailResponse>(
			`${this.basePath}/${id}`,
		);
		return response.data;
	}

	async updateBeneficiary(
		id: string,
		data: BeneficiaryUpdateRequest,
	): Promise<CreateBeneficiaryResponse> {
		const response = await apiClient.patch<CreateBeneficiaryResponse>(
			`${this.basePath}/${id}`,
			data,
		);
		return response.data;
	}

	async deleteBeneficiary(id: string): Promise<void> {
		await apiClient.delete(`${this.basePath}/${id}`);
	}

	async archiveBeneficiary(id: string): Promise<CreateBeneficiaryResponse> {
		const response = await apiClient.patch<CreateBeneficiaryResponse>(
			`${this.basePath}/${id}`,
			{
				status: "inactive",
			},
		);
		return response.data;
	}

	async unarchiveBeneficiary(id: string): Promise<CreateBeneficiaryResponse> {
		const response = await apiClient.patch<CreateBeneficiaryResponse>(
			`${this.basePath}/${id}`,
			{
				status: "active",
			},
		);
		return response.data;
	}

	// --- Fiscal Quotas ---

	async getFiscalQuotas(beneficiaryUuid: string): Promise<FiscalQuota[]> {
		const response = await apiClient.get<FiscalQuota[]>(
			`/beneficiaries/${beneficiaryUuid}/fiscal-quotas`,
		);
		return response.data;
	}

	async createFiscalQuota(
		beneficiaryUuid: string,
		data: CreateFiscalQuotaRequest,
	): Promise<FiscalQuota> {
		const response = await apiClient.post<FiscalQuota>(
			`/beneficiaries/${beneficiaryUuid}/fiscal-quotas`,
			data,
			{ headers: { "Content-Type": "application/json" } },
		);
		return response.data;
	}

	async updateFiscalQuota(
		beneficiaryUuid: string,
		quotaUuid: string,
		data: UpdateFiscalQuotaRequest,
	): Promise<FiscalQuota> {
		const response = await apiClient.patch<FiscalQuota>(
			`/beneficiaries/${beneficiaryUuid}/fiscal-quotas/${quotaUuid}`,
			data,
			{ headers: { "Content-Type": "application/json" } },
		);
		return response.data;
	}

	async deleteFiscalQuota(
		beneficiaryUuid: string,
		quotaUuid: string,
	): Promise<void> {
		await apiClient.delete(
			`/beneficiaries/${beneficiaryUuid}/fiscal-quotas/${quotaUuid}`,
		);
	}

	async getFiscalYearSummary(
		beneficiaryUuid: string,
		year: number,
	): Promise<FiscalYearSummary> {
		const response = await apiClient.get<FiscalYearSummary>(
			`/beneficiaries/${beneficiaryUuid}/fiscal-year-summary?year=${year}`,
		);
		return response.data;
	}

	// --- Bank Certificates ---

	async uploadBankCertificate(
		beneficiaryUuid: string,
		accountUuid: string,
		certificate: File,
	): Promise<DocumentWithReviewResponse> {
		return this.uploadBankCertificateDocument(
			beneficiaryUuid,
			accountUuid,
			certificate,
		);
	}

	async getBankCertificate(
		beneficiaryUuid: string,
		accountUuid: string,
	): Promise<DocumentDownloadResponse> {
		const certificate = await this.findBankCertificate(
			beneficiaryUuid,
			accountUuid,
		);
		if (!certificate) {
			throw new ApiClientError({
				message: "No certificate found for this bank account",
				code: "NOT_FOUND",
				status: 404,
			});
		}
		const response = await apiClient.get<DocumentDownloadResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents/${certificate.document.uuid}/download`,
		);
		return response.data;
	}

	async deleteBankCertificate(
		beneficiaryUuid: string,
		accountUuid: string,
	): Promise<void> {
		const certificate = await this.findBankCertificate(
			beneficiaryUuid,
			accountUuid,
		);
		if (certificate) {
			await apiClient.delete(
				`/beneficiaries/${beneficiaryUuid}/documents/${certificate.document.uuid}`,
			);
		}
	}

	async getBankCertificateDetails(
		beneficiaryUuid: string,
		accountUuid: string,
	): Promise<DocumentWithReviewResponse | null> {
		return this.findBankCertificate(beneficiaryUuid, accountUuid);
	}

	// --- Legal Representatives ---

	async updateLegalRepresentative(
		beneficiaryUuid: string,
		representativeUuid: string,
		data: {
			first_name?: string;
			last_name?: string;
			position?: string;
			dni?: string;
			phone?: string;
			email?: string;
			is_primary?: boolean;
		},
	): Promise<void> {
		await apiClient.patch(
			`/beneficiaries/${beneficiaryUuid}/legal-representatives/${representativeUuid}`,
			data,
			{ headers: { "Content-Type": "application/json" } },
		);
	}

	// --- Bank Accounts ---

	async updateBankAccount(
		beneficiaryUuid: string,
		accountUuid: string,
		data: {
			bank_name?: string;
			iban?: string;
			swift?: string;
			is_primary?: boolean;
		},
	): Promise<void> {
		await apiClient.patch(
			`/beneficiaries/${beneficiaryUuid}/bank-accounts/${accountUuid}`,
			data,
			{
				headers: { "Content-Type": "application/json" },
			},
		);
	}

	// --- CSV Export ---

	async exportToCsv(
		params?: Omit<BeneficiaryListParams, "limit" | "offset">,
	): Promise<void> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.sector) queryParams.append("sector", params.sector);
			if (params.sort_by) queryParams.append("sort_by", params.sort_by);
			if (params.sort_direction)
				queryParams.append("sort_direction", params.sort_direction);
		}

		const url = queryParams.toString()
			? `${this.basePath}/export?${queryParams.toString()}`
			: `${this.basePath}/export`;

		const { blob, filename } = await apiClient.getBlobWithFilename(
			url,
			"beneficiaries.csv",
			{
				headers: { Accept: "text/csv" },
			},
		);

		downloadBlob(blob, filename);
	}

	// --- Document operations ---

	async uploadDocument(
		beneficiaryUuid: string,
		file: File,
		documentType: DocumentType,
		entityType: EntityType,
		entityUuid: string,
		title?: string,
	): Promise<DocumentWithReviewResponse> {
		const formData = new FormData();
		formData.append("document", file);
		formData.append("document_type", documentType);
		formData.append("entity_type", entityType);
		formData.append("entity_uuid", entityUuid);
		if (title) {
			formData.append("title", title);
		}
		const response = await apiClient.upload<DocumentWithReviewResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents`,
			formData,
		);
		return response.data;
	}

	async listDocuments(
		beneficiaryUuid: string,
		params?: ListDocumentsParams,
	): Promise<BeneficiaryPortfolioResponse> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.document_type)
				queryParams.append("document_type", params.document_type);
			if (params.status) queryParams.append("status", params.status);
			if (params.fiscal_year)
				queryParams.append("fiscal_year", params.fiscal_year.toString());
			if (params.search) queryParams.append("search", params.search);
			if (params.limit) queryParams.append("limit", params.limit.toString());
			if (params.offset) queryParams.append("offset", params.offset.toString());
		}
		const url = queryParams.toString()
			? `/beneficiaries/${beneficiaryUuid}/documents?${queryParams.toString()}`
			: `/beneficiaries/${beneficiaryUuid}/documents`;
		const response = await apiClient.get<BeneficiaryPortfolioResponse>(url);
		return response.data;
	}

	async getDocument(
		beneficiaryUuid: string,
		documentUuid: string,
	): Promise<DocumentWithReviewResponse> {
		const response = await apiClient.get<DocumentWithReviewResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents/${documentUuid}`,
		);
		return response.data;
	}

	async getDocumentDownloadUrl(
		beneficiaryUuid: string,
		documentUuid: string,
	): Promise<DocumentDownloadResponse> {
		const response = await apiClient.get<DocumentDownloadResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents/${documentUuid}/download`,
		);
		return response.data;
	}

	async deleteDocument(
		beneficiaryUuid: string,
		documentUuid: string,
	): Promise<void> {
		await apiClient.delete(
			`/beneficiaries/${beneficiaryUuid}/documents/${documentUuid}`,
		);
	}

	async reviewDocument(
		beneficiaryUuid: string,
		documentUuid: string,
		review: ReviewUpdateRequest,
	): Promise<DocumentWithReviewResponse> {
		const response = await apiClient.patch<DocumentWithReviewResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents/${documentUuid}/review`,
			review,
		);
		return response.data;
	}

	async updateDocument(
		beneficiaryUuid: string,
		documentUuid: string,
		data: DocumentUpdateRequest,
	): Promise<DocumentWithReviewResponse> {
		const response = await apiClient.patch<DocumentWithReviewResponse>(
			`/beneficiaries/${beneficiaryUuid}/documents/${documentUuid}`,
			data,
		);
		return response.data;
	}
}
